import MultiTenantCustomerSearch from '@components/CustomerSearch/MultiTenantCustomerSearch';
import { useState } from 'react';
import CustomerDetails from '../CustomerDetails';
import CustomerMfeAdapter from '../CustomerMfeAdapter';
import type { ResolvedCustomer } from '../CustomerMfeAdapter';
import classNames from 'classnames';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import TasksModule from '@modules/TasksModule';
import TaskLoadingOverlay from '@app/layouts/MainLayout/components/TaskLoadingOverlay/TaskLoadingOverlay';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import useFeatureFlag from '@modules/FeatureFlagModule/hooks/useFeatureFlag/useFeatureFlag';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { resolveOrgNodeId } from '@modules/AuthModule/lib/resolveOrgNodeId';
import useBookingRetentionEnabled from '@modules/InboxModule/hooks/useBookingRetentionEnabled';
import styles from './MultiTenantTaskView.module.css';

export type SelectedCustomer = {
  tenantId: string;
  customerId: string;
};

/** customer-mfe's resolved-customer round-trip seed (REQ-TXT-ORGID-006 AC-9).
 * Distinct from SelectedCustomer (the legacy non-MFE search's numeric-tenantId
 * shape) — the MFE branch scopes by Guid. */
type MfeSelectedCustomer = {
  orgNodeId: string;
  customerId: string;
};

interface MultiTenantTaskViewProps {
  taskId: string | undefined;
  /**
   * Whether this view is the selected one. A hidden view stays mounted (to keep
   * its search, customer and booking) but is not displayed and does not guard
   * navigation. Defaults to shown.
   */
  show?: boolean;
  /** Reports whether the MFE has a booking open, hidden or not. MFE branch only. */
  onGuardedChange?: (guarded: boolean) => void;
  /** Called when the CSR confirms a Close from the card's button. MFE branch only. */
  onCloseConfirmed?: () => void;
}

/**
 * This component is used to display the customer search and customer details for a multi-tenant task.
 * A task is multi-tenant if it does not have a tenantId associated with the task.
 *
 */
const MultiTenantTaskView = ({
  taskId,
  show = true,
  onGuardedChange,
  onCloseConfirmed,
}: MultiTenantTaskViewProps) => {
  const [selectedCustomer, setSelectedCustomer] = useState<
    SelectedCustomer | undefined
  >(undefined);
  const [mfeSelectedCustomer, setMfeSelectedCustomer] = useState<
    MfeSelectedCustomer | undefined
  >(undefined);

  const { taskById } = useTasks();
  const { user } = useAuth();
  const task = taskId ? taskById[taskId] : undefined;
  const isRetentionEnabled = useBookingRetentionEnabled();
  const isCustomerMfeEnabled = useFeatureFlag(
    FeatureFlagKeys.featureEnableAgentInboxForCalls,
    { defaultValue: false },
  );

  // Retention on: the pick lives on the task, so a remount or reload reopens on
  // it. Local state wins, so a pick made in this view is never overridden.
  const taskCustomerId: string | undefined = task?.attributes.customerId;
  const taskOrgNodeId: string | undefined = task?.attributes.customerOrgNodeId;
  const taskTenantId: string | undefined = task?.attributes.customerTenantId;
  const seededMfeCustomer: MfeSelectedCustomer | undefined =
    isRetentionEnabled && taskCustomerId && taskOrgNodeId
      ? { orgNodeId: taskOrgNodeId, customerId: taskCustomerId }
      : undefined;
  const seededCustomer: SelectedCustomer | undefined =
    isRetentionEnabled && taskCustomerId && taskTenantId
      ? { tenantId: taskTenantId, customerId: taskCustomerId }
      : undefined;
  const currentMfeCustomer = mfeSelectedCustomer ?? seededMfeCustomer;
  const currentCustomer = selectedCustomer ?? seededCustomer;

  // Campaign attribution belongs to the call's tenant: never pass it when the
  // view is seeded on a customer from another tenant.
  const callTenantOrgNodeId = resolveOrgNodeId(
    user?.org.tenants ?? [],
    task?.tenantId,
  );
  const campaignId =
    !isRetentionEnabled || currentMfeCustomer?.orgNodeId === callTenantOrgNodeId
      ? task?.attributes.campaign?.id
      : undefined;

  const handleBackClick = () => {
    setSelectedCustomer(undefined);

    if (!task) {
      return;
    }

    TasksModule.setCustomerOnTask(task.id, undefined);
  };

  const handleCustomerSelect = (
    customerId: string,
    customerName: string,
    tenantId: string,
  ) => {
    setSelectedCustomer({ tenantId, customerId });

    if (!task) {
      return;
    }

    TasksModule.setCustomerOnTask(task.id, {
      id: customerId,
      name: customerName,
      // Retention on: keep the customer's tenant and org node on the task so
      // the pick survives a remount, a reload and a late tenant arrival.
      ...(isRetentionEnabled
        ? {
            tenantId,
            orgNodeId: resolveOrgNodeId(user?.org.tenants ?? [], tenantId),
          }
        : {}),
    });
  };

  const handleMfeCustomerResolved = (customer: ResolvedCustomer) => {
    setMfeSelectedCustomer(
      customer?.orgNodeId
        ? { orgNodeId: customer.orgNodeId, customerId: customer.id }
        : undefined,
    );

    if (!taskId) {
      return;
    }

    TasksModule.setCustomerOnTask(taskId, customer);
  };

  if (isCustomerMfeEnabled) {
    return (
      // flex-grow-1: this view is a sibling in InboxPage's flex-direction:column
      // Stack — align-items:stretch only affects the cross (horizontal) axis there,
      // so without flex-grow this div (and customer-mfe's height:100% shell inside
      // it) collapses to its content's height instead of filling the page.
      // No p-x-3/p-y-2 here — customer-mfe owns its own internal spacing and
      // should fill this pane edge to edge, unlike the native search/details forms.
      // d-f only while shown, so it cannot defeat the hidden class.
      <div
        className={classNames('of-y-auto', 'flex-grow-1', {
          'd-f': show,
          [styles.MultiTenantTaskViewHidden]: !show,
        })}
      >
        <TaskLoadingOverlay taskId={taskId}>
          {/* mfeSelectedCustomer re-seeds the adapter on re-render once resolved
              — this is the load-bearing half of the AC-9 round-trip: without
              it, a resolved cross-tenant task silently falls back to Mode 1
              (all-tenants search) on the next render instead of staying
              tenant-scoped. */}
          {taskId && (
            <CustomerMfeAdapter
              orgNodeId={currentMfeCustomer?.orgNodeId}
              customerId={currentMfeCustomer?.customerId}
              // Inert until a customer resolves, since the adapter gates the phone
              // seed on orgNodeId and this view has none before then. Once resolved
              // it is what "back to search" returns to, same as the tenant-scoped
              // TaskView (CCPE-1656 Defect B on the cross-tenant path).
              phoneNumber={task?.from}
              campaignId={campaignId}
              callSid={task?.attributes.sessionSid}
              // Only the visible adapter may hold the router blocker.
              guardNavigation={show}
              viewKey={task?.viewKey}
              onCustomerChanged={handleMfeCustomerResolved}
              onGuardedChange={onGuardedChange}
              onCloseConfirmed={onCloseConfirmed}
            />
          )}
        </TaskLoadingOverlay>
      </div>
    );
  }

  return (
    <div
      className={classNames('p-x-3', 'p-y-2', 'of-y-auto', {
        'd-f': show,
        [styles.MultiTenantTaskViewHidden]: !show,
      })}
    >
      <TaskLoadingOverlay taskId={taskId}>
        {currentCustomer && (
          <CustomerDetails
            customerId={currentCustomer.customerId}
            tenantId={currentCustomer.tenantId}
            onBackClick={handleBackClick}
          />
        )}
        <MultiTenantCustomerSearch
          show={!currentCustomer}
          onSelectCustomer={handleCustomerSelect}
        />
      </TaskLoadingOverlay>
    </div>
  );
};

export default MultiTenantTaskView;
